import React, { useCallback, useMemo, useRef } from 'react';

import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import { Button, Grid, InputAdornment, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import { DragHandle, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import indexToLetter from '@dyl/utils/indexToLetter';
import Draggable from 'react-draggable';
import useSize from '@dyl/hooks/useSize';

import Node from '../../Shared/Node';
import useLocalTaskError from '@dyl/pages/Tasks/edit/useLocalTaskError';
import AudioPicker from "@dyl/components/AudioPicker";

const useAnswerStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        flexDirection: 'row',

        '&:not(:hover) $arrowsGroup': {
            opacity: 0,
        }
    },
    arrowsGroup: {
        width: 24,
        height: 56,

        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',

        opacity: 1,
        transitionProperty: 'opacity',
        transitionDuration: '200ms',
    },
    arrow: {
        flex: 1,
    },
    field: {
        flex: 2,
    },
}));

const ELEMENT_COUNT = 6;

export default function Answer({ list, isDistractor = false, index, answerIndex, dropZoneLocationIndex }) {
    const classes = useAnswerStyles();
    const [displayOrder] = useTaskField('items', index, 'answers', answerIndex, 'display_order');

    const [answer, setAnswer, apiError] = useTaskField('items', index, 'answers', answerIndex, 'key_text');
    const onAnswerChange = useChangeEvent(setAnswer);

    const [audioId, setAudioId, audioIdError] = useTaskField('items', index, 'audio_id');
    const [, setItems] = useTaskField('items');

    const punctuationError = (
        /[.!?,:;]\s*$/.test(answer ?? '')
            ? 'Answer should not have ending punctuation - a full stop will be applied automatically.'
            : null
    );
    useLocalTaskError(punctuationError && (
        // E.g. "Error in answer 1-B"
        `Error in answer ${Math.floor(index / 4) + 1}-${indexToLetter(index % 4)}.`
    ));
    const error = punctuationError || apiError;

    const move = useCallback(offset => {
        if (offset === 0) return;

        setItems(prev => {
            const srcIndex = list.findIndex(x => x.index === index && x.answerIndex === answerIndex);
            console.assert(srcIndex !== -1, 'Invalid item');
            const newIndex = srcIndex + offset;
            if (newIndex < 0 || newIndex > 6) return;

            // Swap the elements around.
            // [list[srcIndex], list[newIndex]] = [list[newIndex], list[srcIndex]];

            // Move the element to the new spot and relocate all elements between the old and new location by 1.
            // Splice array and put in the element we want at the appropriate slot.
            list.splice(newIndex, 0, list.splice(srcIndex, 1)[0]);

            return prev.map((item, ii) => ({
                ...item,
                answers: item.answers.map((answer, ai) => ({
                    ...answer,
                    display_order: list.findIndex(x => x.index === ii && x.answerIndex === ai) + 1,
                })),
            }));
        });
    }, [setItems, index]);

    const dragHandleClass = `answerDragHandle-${index}`;

    const containerRef = useRef();
    const { height: containerHeight } = useSize(containerRef);

    const bounds = useMemo(() => ({
        // lower by one as we use 1-indexing for displayOrder due to BE limitations.
        top: (1 - displayOrder) * containerHeight,
        bottom: (ELEMENT_COUNT - displayOrder) * containerHeight,
        left: 0,
        right: 0,
    }), [containerHeight, displayOrder]);

    const label = (type) => isDistractor
        ? 'Distractor text'
        : `${index === 0 ? 'Example' : 'Item ' + index} ${type}`;

    return (
        <Draggable
            container
            handle={`.${dragHandleClass}`}
            position={{ x: 0, y: 0 }}
            axis='y'
            bounds={bounds}
            onStop={(e, data) => {
                const rect = containerRef.current.getBoundingClientRect();
                const delta = data.lastY;

                const indexOffset = Math.round(delta / rect.height);
                if (indexOffset !== 0) {
                    move(indexOffset);
                }
            }}
        >
            <div className={classes.root} ref={containerRef}>
                <Grid item md={5}>
                    <TextField
                        className={classes.field}
                        label={label('Key text')}
                        value={answer}
                        onChange={onAnswerChange}
                        variant='filled'
                        error={!!error}
                        helperText={error || '  '}
                        InputProps={{
                            startAdornment: isDistractor ? <></> : <Node lineId={dropZoneLocationIndex} side='end' /> ,
                            endAdornment: (
                                <InputAdornment position='end' style={{ cursor: 'grab ' }}>
                                    <DragHandle className={dragHandleClass} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid item md={1} className={classes.arrowsGroup}>
                    <Button
                        className={classes.arrow}
                        onClick={() => move(-1)}
                        disabled={displayOrder === 1}
                        tabIndex={-1}
                    >
                        <KeyboardArrowUp fontSize='small' />
                    </Button>
                    <Button
                        className={classes.arrow}
                        onClick={() => move(1)}
                        disabled={displayOrder === ELEMENT_COUNT}
                        tabIndex={-1}
                    >
                        <KeyboardArrowDown fontSize='small' />
                    </Button>
                </Grid>

                <Grid item md={6} className={classes.field}>
                    {!isDistractor &&
                        <AudioPicker
                            value={audioId}
                            onChange={setAudioId}
                            helperText={audioIdError}
                            error={!!audioIdError}
                            label={label('Audio')}
                            fullWidth />
                    }
                </Grid>
            </div>
        </Draggable>
    );
}